/**
 * The workspace's notification bell.
 *
 * Review keeps its own list rather than leaning on Cloud's — see
 * worker/lib/notify.ts for why — and this is the client for it: a badge, a
 * dropdown, a mark-read. The poll only runs while the tab is visible, the same
 * manners as Cloud's bell.
 *
 * One rendering rule matters more than the layout: `actorName` is typed by
 * whoever holds a share link and is never verified. It renders as plain body
 * text — never as chrome, never bolded into looking like the product speaking.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type NotificationWire } from '../api';
import { useRouter } from '../router';
import { ds } from '../ds';
import { formatAgo, mono, monoPlain, RADIUS } from '../theme';

const kindPhrase = (kind: string): string => {
  switch (kind) {
    case 'review_reply':
      return 'replied on';
    case 'review_mention':
      return 'mentioned you on';
    case 'review_approved':
      return 'approved a round of';
    default:
      return 'commented on';
  }
};

export function Bell() {
  const { Icon } = ds();
  const { navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationWire[]>([]);
  const root = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.notifications.list();
      setUnread(res.unread);
      setItems(res.notifications);
    } catch {
      // A missed poll is not news.
    }
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let live = true;
    const tick = async () => {
      if (!live) return;
      if (document.visibilityState === 'visible') await refresh();
      timer = setTimeout(tick, 60_000);
    };
    void tick();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = (n: NotificationWire) => {
    setOpen(false);
    if (!n.readAt) {
      setUnread((u) => Math.max(0, u - 1));
      void api.notifications.markRead(n.id).catch(() => undefined);
    }
    navigate(`/p/${n.projectId}`);
  };

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        onClick={() => {
          setOpen((o) => !o);
          if (!open) void refresh();
        }}
        style={{
          position: 'relative',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          color: 'var(--text-muted)',
          padding: 4,
          display: 'inline-flex'
        }}
      >
        <Icon name="bell" size={15} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -4,
              minWidth: 14,
              height: 14,
              padding: '0 3px',
              borderRadius: 999,
              background: 'var(--primary)',
              color: 'var(--surface-card)',
              font: '700 9px/14px Inter, system-ui, sans-serif',
              textAlign: 'center'
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="mv-pop"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 320,
            maxHeight: 380,
            overflowY: 'auto',
            zIndex: 60,
            background: 'var(--surface-raised, var(--surface-card))',
            border: '1px solid var(--border-subtle)',
            borderRadius: RADIUS.card,
            boxShadow: '0 16px 40px rgba(8,6,16,.5)'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 12px',
              borderBottom: '1px solid var(--border-subtle)'
            }}
          >
            <span style={mono}>Notifications</span>
            <span style={{ flex: 1 }} />
            {unread > 0 && (
              <button
                type="button"
                onClick={() => {
                  setUnread(0);
                  setItems((list) => list.map((n) => ({ ...n, readAt: n.readAt ?? Date.now() / 1000 })));
                  void api.notifications.markRead().catch(() => undefined);
                }}
                style={{ ...mono, fontSize: 9, background: 'none', border: 0, color: 'var(--primary)', cursor: 'pointer' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 && (
            <p style={{ ...monoPlain, padding: '18px 14px' }}>Nothing here yet.</p>
          )}

          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => openItem(n)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                background: n.readAt ? 'none' : 'var(--primary-soft)',
                border: 0,
                borderBottom: '1px solid var(--border-subtle)',
                cursor: 'pointer'
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: 'var(--text-body)'
                }}
              >
                {n.actorName || 'A reviewer'} {kindPhrase(n.kind)} {n.subject}
              </span>
              <span style={{ ...monoPlain, fontSize: 8.5 }}>{formatAgo(n.createdAt)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
